<template>
  <vue-global-frame v-bind:user="User" v-bind:handleLogout="handleLogoutAction" v-bind:handleImpersonation="handleImpersonationAction" :baseURL="baseAPIURL" :authToken="AuthorizationHeader" :environment="Environment" :impersonationInfo="ImpersonationInfo" :viewActiveFeatureStores="ViewActiveFeatureStores" :dealershipUsers="AllUsers" ref="vueglobalframe">
    <div class="main-app-wrapper" slot="app-frame-slot">

      <div id="left-side-nav-container" v-bind:class="{'side-nav-collapsed' : sideNavCollapsed}">
        <main-nav class="main-nav-container" v-bind:class="{'side-nav-collapsed' : sideNavCollapsed}" :sideNavCollapsed='sideNavCollapsed' :user='User'></main-nav>
      </div>

      <div id="main-container" v-bind:class="{'side-nav-collapsed' : sideNavCollapsed}" v-bind:style="{'right': paddingRight}">
        <div class="contrast-overlay" v-if="showContrastOverlay"></div>
        <router-view id='mainRouterContainer'></router-view>
      </div>

      <div id="right-side-nav-container" v-bind:style="{'width' : rightNavWidth}" v-bind:class="rightNavClass">
        <right-nav-section class="right-side-nav-container"></right-nav-section>
      </div>

      <session-expiration-modal v-if="ShowSessionExpirationModal"></session-expiration-modal>
      <session-expiration-info></session-expiration-info>

    </div>
  </vue-global-frame>
</template>

<script>
import { RightNavMode, RightNavContentType } from "@/lib/utilities/enums";

import EventBus from "@/lib/utilities/eventBus.js";

import { mapState, mapGetters } from "vuex";
import { webAuth } from "@/auth/index";

export default {
  name: "web",
  components: {
    MainNav: () => import("@/components/nav/MainNav.vue"),
    VueGlobalFrame: () => import("@autoalert/vueglobalframe"),
    RightNavSection: () => import("@/components/nav/RightNavSection.vue"),
    SessionExpirationModal: () =>
      import("@/components/modals/SessionExpirationModal.vue"),
    SessionExpirationInfo: () =>
      import("@/components/shared/SessionExpirationInfo.vue"),
  },
  created() {
    if (!this.$listeners || !this.$listeners.sendNotificationToGlobalFrame) {
      EventBus.$on(
        "sendNotificationToGlobalFrame",
        this.globalFrameNotificationSender
      );
    }
  },
  beforeDestroy() {
    EventBus.$off(
      "sendNotificationToGlobalFrame",
      this.globalFrameNotificationSender
    );
  },
  data() {
    return {
      windowWidth: 1200,
      baseAPIURL: process.env.MOTOFUZE_API,
    };
  },
  mounted: function () {
    window.addEventListener("resize", this.calculateWindowWidth);
    this.calculateWindowWidth();
  },
  computed: {
    ...mapState({
      RightNavSection: (state) => state.RightNavSection.RightNavSection,
      ShowSessionExpirationModal: (state) =>
        state.Authentication.ShowSessionExpirationModal,
    }),
    ...mapGetters([
      "User",
      "AuthorizationHeader",
      "sideNavCollapsed",
      "Environment",
      "ImpersonationInfo",
      "ViewActiveFeatureStores",
      "AllUsers"
    ]),
    rightNavWidth: function () {
      let rnWidth = "0px";
      if (this.showRightNavSection) {
        if (this.RightNavSection.Mode == RightNavMode.Overlay)
          rnWidth = "500px";
        else if (this.RightNavSection.Mode == RightNavMode.ExtendedOverlay)
          rnWidth = "65%";
        else if (this.RightNavSection.Mode == RightNavMode.PanelOverlay)
          rnWidth = `calc(100% - ${
            this.sideNavCollapsed ? "44px" : "300px"
          } - 350px - 30px)`;
        else if (this.RightNavSection.Mode == RightNavMode.FullOverlay)
          rnWidth = `calc(100% - ${this.sideNavCollapsed ? "44px" : "300px"})`;
        else {
          let leftNavWidth = this.sideNavCollapsed ? 0 : 300;
          var calcWidth = Math.floor((this.windowWidth - leftNavWidth) / 3);
          rnWidth = calcWidth < 400 ? 400 : calcWidth > 550 ? 550 : calcWidth;
          return `${rnWidth}px`;
        }
      }
      return rnWidth;
    },
    paddingRight: function () {
      return this.RightNavSection.Mode == RightNavMode.Normal
        ? this.rightNavWidth
        : "0px";
    },
    rightNavClass() {
      let rnClass = this.showRightNavSection ? "is-visible " : "";

      if (this.RightNavSection.Mode == RightNavMode.ExtendedOverlay)
        rnClass += "extended-overlay ";
      else if (this.RightNavSection.Mode == RightNavMode.PanelOverlay)
        rnClass += "panel-overlay ";

      if (this.showContrastOverlay) rnClass += "contrast-overlay";

      return rnClass;
    },
    showRightNavSection: function () {
      return this.RightNavSection && this.RightNavSection.Type > 0;
    },
    showContrastOverlay() {
      return (
        this.RightNavSection &&
        this.RightNavSection.Type == RightNavContentType.LeadSource
      );
    },
  },
  methods: {
    calculateWindowWidth() {
      this.windowWidth = document.documentElement.clientWidth;
    },
    handleLogoutAction() {
      // console.log("Handle logout action");
      webAuth.logout();
    },
    handleImpersonationAction() {
      webAuth.endImpersonation();
    },
    globalFrameNotificationSender(notificationPayload) {
      try {
        if (this.$refs.vueglobalframe)
          this.$refs.vueglobalframe.handlePubNubNotification(
            notificationPayload
          );
      } catch (err) {
        console.log("Error handling PubNub notification in frame: ", err);
      }
    },
  },
};
</script>

<style lang='scss' scoped>
.main-app-wrapper {
  position: relative;

  #left-side-nav-container {
    width: 300px;
    background-color: $white;
    position: absolute;
    top: 0;
    left: 0;
    border-right: 1px solid $gray-lt;
    transition: $Transition-speed; // box-shadow: 0px 0px 20px 0px #BDC0C2;
    &.side-nav-collapsed {
      width: 44px;
    }
  }

  #main-container {
    position: absolute;
    top: 0;
    left: 300px;
    right: 0;
    bottom: 0;
    transition: $Transition-speed;

    &.side-nav-collapsed {
      left: 44px;
    }

    .contrast-overlay {
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      z-index: 11;
      background: rgba(0, 0, 0, 0.65);
    }

    #mainRouterContainer {
      height: 100%;
      overflow-x: hidden;
    }
  }

  #right-side-nav-container {
    overflow-x: hidden;
    overflow-y: auto;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 11;
    transition: width 0.3s, height 1s, ease; /*Changing transition because of the unwanted shaking effect*/
    background: #ffffff;
    box-shadow: -5px 0 10px rgba(68, 75, 79, 0.5);

    &.extended-overlay,
    &.panel-overlay {
      min-width: 800px;
    }

    &.contrast-overlay {
      box-shadow: none;
    }
  }

  #left-side-nav-container,
  #main-container,
  #right-side-nav-container {
    height: calc(100vh - #{$Global-frame-header-height});
  }
}
</style>



// WEBPACK FOOTER //
// src/views/Web/Web.vue